import React, { useEffect, useState, useCallback } from 'react';
import Papa from 'papaparse';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function Mailboxes() {
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [smtpModalOpen, setSmtpModalOpen] = useState(false);
  const [sharedModalOpen, setSharedModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkRefreshing, setBulkRefreshing] = useState(false);

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (visibleIds) => {
    setSelectedIds(prev => {
      const allSelected = visibleIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const runBulkRefresh = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkRefreshing(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/bulk-refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids }),
      });
      const d = await res.json();
      if (!res.ok) { alert('Refresh failed: ' + (d.error || res.status)); return; }
      alert(`Refreshed ${d.total}: ${d.healed} healed, ${d.failed} still failing.`);
      clearSelection();
      fetchMailboxes();
    } catch (e) {
      alert('Refresh failed: ' + e.message);
    } finally {
      setBulkRefreshing(false);
    }
  };

  const fetchMailboxes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const url = isGlobalMode
        ? `${API_BASE}/admin/mailboxes`
        : `${API_BASE}/clients/${clientId}/mailboxes`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      setMailboxes(Array.isArray(data) ? data : data.mailboxes || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, isGlobalMode, getToken]);

  useEffect(() => {
    if (isGlobalMode || clientId) fetchMailboxes();
  }, [isGlobalMode, clientId, fetchMailboxes]);

  const filtered = mailboxes.filter(m => {
    if (!query) return true;
    const q = query.toLowerCase();
    const clientText = (m.assigned_clients || [])
      .map(c => `${c.name || ''} ${c.sector || ''}`)
      .join(' ')
      .toLowerCase();
    return (
      (m.email_address || '').toLowerCase().includes(q) ||
      (m.display_name || '').toLowerCase().includes(q) ||
      clientText.includes(q)
    );
  });

  const statusColour = (s) => {
    if (s === 'active') return '#1a7f3c';
    if (s === 'error' || s === 'disconnected') return '#b91c1c';
    if (s === 'paused') return '#a16207';
    return '#666';
  };

  if (loading) return <div style={{ padding: 24 }}>Loading mailboxes…</div>;
  if (error) return <div style={{ padding: 24, color: '#b91c1c' }}>Error: {error}</div>;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>
          Mailboxes {isGlobalMode
            ? <span style={{ fontSize: 13, color: '#666', fontWeight: 400, marginLeft: 8 }}>· Platform pool</span>
            : <span style={{ fontSize: 13, color: '#666', fontWeight: 400, marginLeft: 8 }}>· Available to this workspace</span>}
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search mailboxes…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d4d4d4', borderRadius: 6, fontSize: 14, width: 220 }}
          />
          {isGlobalMode && (
            <>
              <a href={`${API_BASE}/oauth/google/connect`}
                 style={{ padding: '8px 14px', border: '1px solid #d4d4d4', borderRadius: 6, fontSize: 14, color: '#222', textDecoration: 'none', background: '#fff' }}>
                + Google
              </a>
              <a href={`${API_BASE}/oauth/microsoft/connect`}
                 style={{ padding: '8px 14px', border: '1px solid #d4d4d4', borderRadius: 6, fontSize: 14, color: '#222', textDecoration: 'none', background: '#fff' }}>
                + Microsoft
              </a>
              <a href={`${API_BASE}/oauth/zoho/connect`}
                 style={{ padding: '8px 14px', border: '1px solid #d4d4d4', borderRadius: 6, fontSize: 14, color: '#222', textDecoration: 'none', background: '#fff' }}>
                + Zoho
              </a>
              <button type="button" onClick={() => setSmtpModalOpen(true)}
                 style={{ padding: '8px 14px', border: '1px solid #d4d4d4', borderRadius: 6, fontSize: 14, color: '#222', background: '#fff', cursor: 'pointer' }}>
                + SMTP
              </button>
              <button type="button" onClick={() => setSharedModalOpen(true)}
                 style={{ padding: '8px 14px', border: '1px solid #d4d4d4', borderRadius: 6, fontSize: 14, color: '#222', background: '#fff', cursor: 'pointer' }}>
                + Zoho Shared
              </button>
            </>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#666', border: '1px dashed #d4d4d4', borderRadius: 8 }}>
          {mailboxes.length === 0 ? 'No mailboxes connected yet.' : 'No mailboxes match that search.'}
        </div>
      ) : (
        <>
        {selectedIds.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.7rem 0.9rem', background: '#1A1A1A', borderRadius: 8, marginBottom: 12 }}>
            <div style={{ fontSize: '0.82rem', color: '#fff', fontWeight: 600, marginRight: 4 }}>{selectedIds.size} selected</div>
            <button onClick={() => setAssignModalOpen(true)}
              style={{ fontSize: '0.78rem', color: '#fff', background: '#553C9A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Add to pool
            </button>
            <button onClick={() => setBulkEditOpen(true)}
              style={{ fontSize: '0.78rem', color: '#fff', background: '#0A66C2', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Bulk edit
            </button>
            <button onClick={runBulkRefresh} disabled={bulkRefreshing}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: '0.78rem', color: '#fff', background: '#0E7C86', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: bulkRefreshing ? 'default' : 'pointer', fontWeight: 500, whiteSpace: 'nowrap', opacity: bulkRefreshing ? 0.75 : 1 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ animation: bulkRefreshing ? 'ce-spin 0.8s linear infinite' : 'none' }}>
                <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                <polyline points="21 3 21 9 15 9" />
              </svg>
              {bulkRefreshing ? 'Refreshing…' : 'Refresh tokens'}
            </button>
            <style>{`@keyframes ce-spin { to { transform: rotate(360deg); } }`}</style>
            <button onClick={clearSelection}
              style={{ fontSize: '0.78rem', color: '#cfcfcf', background: 'transparent', border: '1px solid #444', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Clear
            </button>
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e5e5', color: '#666' }}>
              <th style={{ padding: '10px 12px', width: 32 }}>
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && filtered.every(m => selectedIds.has(m.id))}
                  onChange={() => toggleSelectAll(filtered.map(m => m.id))}
                />
              </th>
              {isGlobalMode && <th style={{ padding: '10px 12px', fontWeight: 500 }}>Client</th>}
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Email</th>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Display name</th>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Provider</th>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Owner</th>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Status</th>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Daily cap</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(m => (
              <tr
                key={m.id}
                onClick={() => {
                  const base = isGlobalMode ? '/mail/mailboxes' : `/clients/${clientId}/mail/mailboxes`;
                  navigate(`${base}/${m.id}`);
                }}
                style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer', background: selectedIds.has(m.id) ? '#FBF1F1' : 'transparent' }}
                onMouseEnter={(e) => { if (!selectedIds.has(m.id)) e.currentTarget.style.background = '#FAFAFA'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = selectedIds.has(m.id) ? '#FBF1F1' : 'transparent'; }}
              >
                <td style={{ padding: '10px 12px', width: 32 }} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(m.id)}
                    onChange={() => toggleSelected(m.id)}
                  />
                </td>
                {isGlobalMode && (
                  <td style={{ padding: '10px 12px' }} onClick={(e) => e.stopPropagation()}>
                    {(m.assigned_clients && m.assigned_clients.length) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {m.assigned_clients.map(c => (
                          <Link key={c.id} to={`/clients/${c.id}/mail/mailboxes`}
                                style={{ color: '#8B1A1A', textDecoration: 'none', fontSize: 13 }}>
                            {c.name}{c.sector ? <span style={{ color: '#888', marginLeft: 4 }}>· {c.sector}</span> : null}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#aaa', fontSize: 13 }}>Unassigned</span>
                    )}
                  </td>
                )}
                <td style={{ padding: '10px 12px' }}>{m.email_address}</td>
                <td style={{ padding: '10px 12px' }}>{m.display_name || '—'}</td>
                <td style={{ padding: '10px 12px', textTransform: 'capitalize' }}>{m.provider || '—'}</td>
                <td style={{ padding: '10px 12px', textTransform: 'capitalize' }}>{m.owner_type || 'client'}</td>
                <td style={{ padding: '10px 12px' }} onClick={(e) => e.stopPropagation()}>
                  <span style={{ color: statusColour(m.status), fontWeight: 500 }}>
                    {(m.status === 'error' || m.status === 'disconnected') ? 'Disconnected' : (m.status || 'unknown')}
                  </span>
                  {isGlobalMode && (m.status === 'error' || m.status === 'disconnected') && m.provider && m.provider !== 'smtp' && (
                    <a href={`${API_BASE}/oauth/${m.provider}/connect`}
                       style={{ marginLeft: 10, fontSize: 12, color: '#8B1A1A', textDecoration: 'underline' }}>
                      Reconnect
                    </a>
                  )}
                </td>
                <td style={{ padding: '10px 12px' }}>{m.daily_cap ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}
      {assignModalOpen && (
        <AssignToPoolModal
          mailboxIds={Array.from(selectedIds)}
          clientId={clientId}
          isGlobalMode={isGlobalMode}
          getToken={getToken}
          onClose={() => setAssignModalOpen(false)}
          onDone={() => { setAssignModalOpen(false); clearSelection(); fetchMailboxes(); }}
        />
      )}
      {bulkEditOpen && (
        <BulkEditModal
          mailboxIds={Array.from(selectedIds)}
          onClose={() => setBulkEditOpen(false)}
          onDone={() => { setBulkEditOpen(false); clearSelection(); fetchMailboxes(); }}
        />
      )}
    {smtpModalOpen && (
        <SmtpModal onClose={() => setSmtpModalOpen(false)} onImported={() => { setSmtpModalOpen(false); fetchMailboxes(); }} />
      )}
      {sharedModalOpen && (
        <SharedZohoModal onClose={() => setSharedModalOpen(false)} onImported={() => { setSharedModalOpen(false); fetchMailboxes(); }} />
      )}
    </div>
  );
}


// ── SMTP Modal — single + bulk CSV import ─────────────────────────────────
function SmtpModal({ onClose, onImported }) {
  const { getToken } = useAuth();
  const [mode, setMode] = useState('single'); // 'single' or 'bulk'

  // Single-form state
  const [emailAddress, setEmailAddress] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState(993);
  const [imapUsername, setImapUsername] = useState('');
  const [imapPassword, setImapPassword] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  // Bulk state
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkParseError, setBulkParseError] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  async function runTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/smtp/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          smtp_host: smtpHost, smtp_port: parseInt(smtpPort) || 587,
          smtp_username: smtpUsername || emailAddress, smtp_password: smtpPassword,
          imap_host: imapHost || null, imap_port: parseInt(imapPort) || 993,
          imap_username: imapUsername || emailAddress, imap_password: imapPassword || smtpPassword,
        }),
      });
      const data = await res.json();
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, errors: { network: e.message } });
    } finally {
      setTesting(false);
    }
  }

  async function saveSingle() {
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/smtp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          email_address: emailAddress,
          display_name: displayName || null,
          smtp_host: smtpHost, smtp_port: parseInt(smtpPort) || 587,
          smtp_username: smtpUsername || emailAddress, smtp_password: smtpPassword,
          imap_host: imapHost || null, imap_port: parseInt(imapPort) || 993,
          imap_username: imapUsername || emailAddress, imap_password: imapPassword || smtpPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) { alert('Save failed: ' + (data.error || res.status)); return; }
      onImported();
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  function handleCsvFile(file) {
    setBulkParseError('');
    setBulkResults(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors && result.errors.length) {
          setBulkParseError(result.errors[0].message);
          return;
        }
        // Map ManyReach columns to our schema
        const mapped = (result.data || []).map(r => {
          const fromName = (r['FromName'] || '').trim();
          const first = (r['First Name'] || '').trim();
          const last = (r['Last Name'] || '').trim();
          const display = fromName || [first, last].filter(Boolean).join(' ');
          return {
            email_address: (r['Email'] || '').trim(),
            display_name: display || null,
            signature_html: (r['Signature'] || '').trim() || null,
            smtp_host: (r['SMTP Host'] || '').trim(),
            smtp_port: parseInt(r['SMTP Port']) || 587,
            smtp_username: (r['SMTP Username'] || '').trim() || null,
            smtp_password: (r['SMTP Password'] || '').trim(),
            imap_host: (r['IMAP Host'] || '').trim() || null,
            imap_port: parseInt(r['IMAP Port']) || 993,
            imap_username: (r['IMAP Username'] || '').trim() || null,
            imap_password: (r['IMAP Password'] || '').trim() || null,
            daily_cap: parseInt(r['Daily Sending Limit']) || 50,
            daily_cap_ceiling: parseInt(r['Maximum Sending Limit']) || 500,
            ramp_pct: parseInt(r['Limit Increase Percentage']) || 20,
            warmup_enabled: (r['Warmup Enabled'] || '').trim().toUpperCase() === 'TRUE',
          };
        }).filter(r => r.email_address);
        setBulkRows(mapped);
      },
      error: (err) => setBulkParseError(err.message),
    });
  }

  async function runBulkImport() {
    if (!bulkRows.length) return;
    setBulkImporting(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/smtp/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rows: bulkRows }),
      });
      const data = await res.json();
      setBulkResults(data);
    } catch (e) {
      setBulkResults({ ok: false, error: e.message });
    } finally {
      setBulkImporting(false);
    }
  }

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 12, fontWeight: 500, color: '#444', marginBottom: 4, display: 'block' };
  const sectionTitle = { fontSize: 13, fontWeight: 600, color: '#222', marginTop: 16, marginBottom: 8 };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px', overflowY: 'auto' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 640, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #EEE', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Add SMTP/IMAP mailbox</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid #EEE' }}>
          <button
            onClick={() => setMode('single')}
            style={{ flex: 1, padding: '10px 14px', background: mode === 'single' ? '#f7f4f0' : 'white', border: 'none', borderBottom: mode === 'single' ? '2px solid #8B1A1A' : '2px solid transparent', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
          >Single mailbox</button>
          <button
            onClick={() => setMode('bulk')}
            style={{ flex: 1, padding: '10px 14px', background: mode === 'bulk' ? '#f7f4f0' : 'white', border: 'none', borderBottom: mode === 'bulk' ? '2px solid #8B1A1A' : '2px solid transparent', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
          >Bulk import (CSV)</button>
        </div>

        <div style={{ padding: 20, maxHeight: '70vh', overflowY: 'auto' }}>
          {mode === 'single' ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Email address</label>
                  <input style={inputStyle} value={emailAddress} onChange={e => setEmailAddress(e.target.value)} placeholder="user@example.com" />
                </div>
                <div>
                  <label style={labelStyle}>Display name (optional)</label>
                  <input style={inputStyle} value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Jane Doe" />
                </div>
              </div>

              <div style={sectionTitle}>SMTP (sending)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Host</label>
                  <input style={inputStyle} value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="smtp.example.com" />
                </div>
                <div>
                  <label style={labelStyle}>Port</label>
                  <input style={inputStyle} type="number" value={smtpPort} onChange={e => setSmtpPort(e.target.value)} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
                <div>
                  <label style={labelStyle}>Username (defaults to email)</label>
                  <input style={inputStyle} value={smtpUsername} onChange={e => setSmtpUsername(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Password</label>
                  <input style={inputStyle} type="password" value={smtpPassword} onChange={e => setSmtpPassword(e.target.value)} />
                </div>
              </div>

              <div style={sectionTitle}>IMAP (receiving)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Host</label>
                  <input style={inputStyle} value={imapHost} onChange={e => setImapHost(e.target.value)} placeholder="imap.example.com" />
                </div>
                <div>
                  <label style={labelStyle}>Port</label>
                  <input style={inputStyle} type="number" value={imapPort} onChange={e => setImapPort(e.target.value)} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
                <div>
                  <label style={labelStyle}>Username (defaults to email)</label>
                  <input style={inputStyle} value={imapUsername} onChange={e => setImapUsername(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Password (defaults to SMTP password)</label>
                  <input style={inputStyle} type="password" value={imapPassword} onChange={e => setImapPassword(e.target.value)} />
                </div>
              </div>

              {testResult && (
                <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 6, fontSize: 13, background: testResult.ok ? '#E8F5EC' : '#FFEBEB', color: testResult.ok ? '#1a7f3c' : '#b91c1c' }}>
                  {testResult.ok
                    ? 'Connection test succeeded.'
                    : `Connection test failed: ${JSON.stringify(testResult.errors || testResult.error || testResult)}`}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                <button onClick={onClose} style={{ padding: '8px 14px', background: '#fff', border: '1px solid #d4d4d4', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <button onClick={runTest} disabled={testing || !smtpHost || !smtpPassword} style={{ padding: '8px 14px', background: '#fff', border: '1px solid #d4d4d4', borderRadius: 6, cursor: 'pointer', fontSize: 13, opacity: testing || !smtpHost || !smtpPassword ? 0.5 : 1 }}>
                  {testing ? 'Testing…' : 'Test connection'}
                </button>
                <button onClick={saveSingle} disabled={saving || !testResult || !testResult.ok} style={{ padding: '8px 14px', background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, opacity: saving || !testResult || !testResult.ok ? 0.5 : 1 }}>
                  {saving ? 'Saving…' : 'Save mailbox'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, color: '#444', marginBottom: 14, lineHeight: 1.55 }}>
                Download the template, fill in one row per mailbox, then upload it here.
                Each row is tested against the SMTP and IMAP servers before being saved.
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={async () => {
                      const token = await getToken();
                      const r = await fetch(`${API_BASE}/mailboxes/smtp/template.csv`, { headers: { Authorization: `Bearer ${token}` } });
                      if (!r.ok) { alert("Could not fetch template: " + r.status); return; }
                      const blob = await r.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "smtp-mailboxes-template.csv";
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                    }}
                    style={{ display: "inline-block", padding: "6px 12px", background: "#fff", border: "1px solid #d4d4d4", borderRadius: 6, fontSize: 13, color: "#222", cursor: "pointer" }}
                  >
                    Download CSV template
                  </button>
                </div>
              </div>
              <input
                type="file" accept=".csv,text/csv"
                onChange={e => { const f = e.target.files && e.target.files[0]; if (f) handleCsvFile(f); }}
                style={{ marginBottom: 12 }}
              />
              {bulkParseError && (
                <div style={{ padding: '10px 12px', borderRadius: 6, fontSize: 13, background: '#FFEBEB', color: '#b91c1c', marginBottom: 12 }}>
                  CSV parse error: {bulkParseError}
                </div>
              )}
              {bulkRows.length > 0 && !bulkResults && (
                <>
                  <div style={{ fontSize: 13, color: '#444', marginBottom: 10 }}>
                    Parsed <strong>{bulkRows.length}</strong> rows. Review before importing — each row is tested against the SMTP/IMAP servers before saving.
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, marginBottom: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#fafafa', textAlign: 'left', borderBottom: '1px solid #eee' }}>
                          <th style={{ padding: '6px 10px', fontWeight: 500 }}>Email</th>
                          <th style={{ padding: '6px 10px', fontWeight: 500 }}>SMTP host</th>
                          <th style={{ padding: '6px 10px', fontWeight: 500 }}>IMAP host</th>
                          <th style={{ padding: '6px 10px', fontWeight: 500 }}>Daily cap</th>
                          <th style={{ padding: '6px 10px', fontWeight: 500 }}>Warmup</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkRows.slice(0, 50).map((r, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                            <td style={{ padding: '6px 10px' }}>{r.email_address}</td>
                            <td style={{ padding: '6px 10px', color: '#666' }}>{r.smtp_host}:{r.smtp_port}</td>
                            <td style={{ padding: '6px 10px', color: '#666' }}>{r.imap_host || '—'}:{r.imap_port}</td>
                            <td style={{ padding: '6px 10px', color: '#666' }}>{r.daily_cap}</td>
                            <td style={{ padding: '6px 10px', color: '#666' }}>{r.warmup_enabled ? 'On' : 'Off'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bulkRows.length > 50 && (
                      <div style={{ padding: '6px 10px', fontSize: 11, color: '#888' }}>Showing first 50 of {bulkRows.length}.</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ padding: '8px 14px', background: '#fff', border: '1px solid #d4d4d4', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                    <button onClick={runBulkImport} disabled={bulkImporting} style={{ padding: '8px 14px', background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, opacity: bulkImporting ? 0.5 : 1 }}>
                      {bulkImporting ? `Importing ${bulkRows.length}…` : `Import ${bulkRows.length} mailboxes`}
                    </button>
                  </div>
                </>
              )}
              {bulkResults && (
                <>
                  <div style={{ fontSize: 13, color: '#444', marginBottom: 10 }}>
                    Import complete: <strong style={{ color: '#1a7f3c' }}>{bulkResults.succeeded || 0} succeeded</strong>
                    {bulkResults.failed > 0 && <>, <strong style={{ color: '#b91c1c' }}>{bulkResults.failed} failed</strong></>}
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, marginBottom: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#fafafa', textAlign: 'left', borderBottom: '1px solid #eee' }}>
                          <th style={{ padding: '6px 10px', fontWeight: 500 }}>Email</th>
                          <th style={{ padding: '6px 10px', fontWeight: 500 }}>Status</th>
                          <th style={{ padding: '6px 10px', fontWeight: 500 }}>Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(bulkResults.results || []).map((r, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                            <td style={{ padding: '6px 10px' }}>{r.email_address}</td>
                            <td style={{ padding: '6px 10px', color: r.status === 'ok' ? '#1a7f3c' : '#b91c1c' }}>{r.status}</td>
                            <td style={{ padding: '6px 10px', color: '#666' }}>{r.error || (r.mailbox_id ? `id ${r.mailbox_id}` : '')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onImported} style={{ padding: '8px 14px', background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Done</button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AssignToPoolModal({ mailboxIds, clientId, isGlobalMode, getToken, onClose, onDone }) {
  const [clients, setClients] = useState([]);
  const [pickedClientId, setPickedClientId] = useState(isGlobalMode ? '' : clientId);
  const [pools, setPools] = useState([]);
  const [pickedPoolId, setPickedPoolId] = useState('');
  const [creatingNew, setCreatingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isGlobalMode) return;
    (async () => {
      try {
        const token = await getToken();
        const r = await fetch(`${API_BASE}/clients`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(`Could not load clients (${r.status})`);
        const data = await r.json();
        setClients(Array.isArray(data) ? data : data.clients || []);
      } catch (e) { setError(e.message); }
    })();
  }, [isGlobalMode, getToken]);

  useEffect(() => {
    if (!pickedClientId) { setPools([]); return; }
    (async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const r = await fetch(`${API_BASE}/clients/${pickedClientId}/pools`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(`Could not load pools (${r.status})`);
        const data = await r.json();
        setPools(Array.isArray(data) ? data : []);
        setPickedPoolId('');
        setCreatingNew(false);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [pickedClientId, getToken]);

  const canSubmit = pickedClientId && (creatingNew ? newName.trim().length > 0 : pickedPoolId);

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const body = creatingNew
        ? { new_pool: { name: newName.trim(), description: newDesc.trim() || null }, mailbox_ids: mailboxIds }
        : { pool_id: parseInt(pickedPoolId), mailbox_ids: mailboxIds };
      const r = await fetch(`${API_BASE}/clients/${pickedClientId}/pools/bulk-add-mailboxes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 };
  const card = { background: 'white', borderRadius: 10, padding: 24, width: 460, maxWidth: '92vw' };
  const lbl = { display: 'block', fontSize: 12, color: '#666', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 };
  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #d4d4d4', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const btnP = { background: '#8B1A1A', color: 'white', border: 'none', padding: '8px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer' };
  const btnS = { background: 'white', color: '#222', border: '1px solid #d4d4d4', padding: '8px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer' };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Add to pool</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>{mailboxIds.length} mailbox{mailboxIds.length === 1 ? '' : 'es'} selected</div>

        {isGlobalMode && (
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Client</label>
            <select value={pickedClientId} onChange={(e) => setPickedClientId(e.target.value)} style={inp}>
              <option value="">Select a client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {pickedClientId && (
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Pool</label>
            {loading ? (
              <div style={{ fontSize: 13, color: '#888' }}>Loading pools…</div>
            ) : (
              <>
                {!creatingNew && (
                  <select value={pickedPoolId} onChange={(e) => setPickedPoolId(e.target.value)} style={inp}>
                    <option value="">{pools.length === 0 ? 'No pools yet — create one below' : 'Select a pool…'}</option>
                    {pools.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
                <div style={{ marginTop: 8 }}>
                  {!creatingNew ? (
                    <button type="button" onClick={() => { setCreatingNew(true); setPickedPoolId(''); }} style={{ background: 'transparent', border: 'none', color: '#8B1A1A', fontSize: 13, cursor: 'pointer', padding: 0 }}>
                      + Create new pool
                    </button>
                  ) : (
                    <div style={{ border: '1px solid #EAE6E1', borderRadius: 6, padding: 12, background: '#FAFAFA' }}>
                      <label style={lbl}>New pool name</label>
                      <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. UK accountants outbound" style={{ ...inp, marginBottom: 10 }} autoFocus />
                      <label style={lbl}>Description (optional)</label>
                      <input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} style={inp} />
                      <button type="button" onClick={() => { setCreatingNew(false); setNewName(''); setNewDesc(''); }} style={{ background: 'transparent', border: 'none', color: '#666', fontSize: 12, cursor: 'pointer', padding: 0, marginTop: 8 }}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {error && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={btnS}>Cancel</button>
          <button onClick={submit} disabled={!canSubmit || saving} style={{ ...btnP, opacity: (!canSubmit || saving) ? 0.5 : 1 }}>
            {saving ? 'Adding…' : 'Add to pool'}
          </button>
        </div>
      </div>
    </div>
  );
}


function SharedZohoModal({ onClose, onImported }) {
  const { getToken } = useAuth();
  const [parents, setParents] = useState([]);
  const [parentId, setParentId] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await fetch(`${API_BASE}/admin/mailboxes`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json();
        const all = Array.isArray(data) ? data : data.mailboxes || [];
        const zohoParents = all.filter(m => m.provider === 'zoho' && !m.parent_mailbox_id);
        setParents(zohoParents);
        if (zohoParents.length) setParentId(String(zohoParents[0].id));
      } catch (e) { /* ignore */ }
    })();
  }, [getToken]);

  function parseLines() {
    return text.split('\n').map(l => l.trim()).filter(Boolean).map(line => {
      const parts = line.split(',');
      const email = (parts[0] || '').trim();
      const name = (parts.slice(1).join(',') || '').trim();
      return { email_address: email, display_name: name || null };
    }).filter(x => x.email_address);
  }

  async function save() {
    const mailboxes = parseLines();
    if (!parentId) { alert('Pick a parent licensed mailbox'); return; }
    if (!mailboxes.length) { alert('Enter at least one shared mailbox address'); return; }
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/zoho/shared`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ parent_mailbox_id: parseInt(parentId), mailboxes }),
      });
      const data = await res.json();
      if (!res.ok) { alert('Failed: ' + (data.error || res.status)); return; }
      setResults(data);
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 24, width: 560, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Add Zoho shared mailboxes</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer' }}>&times;</button>
        </div>
        <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
          Shared mailboxes send via a parent licensed user (send-as) over the Zoho API. Pick the licensed mailbox, then paste one shared address per line as <code>email, Display Name</code>.
        </p>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Parent licensed mailbox</label>
        <select value={parentId} onChange={e => setParentId(e.target.value)}
          style={{ width: '100%', padding: 8, marginTop: 4, marginBottom: 14, borderRadius: 6, border: '1px solid #d4d4d4', fontSize: 14 }}>
          {parents.length === 0 && <option value="">No Zoho licensed mailbox connected</option>}
          {parents.map(p => <option key={p.id} value={p.id}>{p.email_address}</option>)}
        </select>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Shared mailboxes (one per line: email, Display Name)</label>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={8}
          placeholder={"mm@webcitegroup.co.uk, Mitchell Morton\nhc@webcitegroup.co.uk, Harry Clark"}
          style={{ width: '100%', padding: 8, marginTop: 4, marginBottom: 14, borderRadius: 6, border: '1px solid #d4d4d4', fontSize: 13, fontFamily: 'monospace' }} />
        {results && (
          <div style={{ fontSize: 13, marginBottom: 14, padding: 10, background: '#f6f6f6', borderRadius: 6 }}>
            Imported: {results.succeeded} ok, {results.failed} failed.
            {results.results && results.results.filter(r => r.status !== 'ok').map((r, i) => (
              <div key={i} style={{ color: '#8B1A1A' }}>{r.email_address}: {r.error}</div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', border: '1px solid #d4d4d4', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
            {results ? 'Close' : 'Cancel'}
          </button>
          {!results && (
            <button onClick={save} disabled={saving} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#8B1A1A', color: '#fff', cursor: 'pointer' }}>
              {saving ? 'Saving...' : 'Add mailboxes'}
            </button>
          )}
          {results && (
            <button onClick={onImported} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#8B1A1A', color: '#fff', cursor: 'pointer' }}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function BulkEditModal({ mailboxIds, onClose, onDone }) {
  const { getToken } = useAuth();
  // Cap fields (mailboxes table)
  const [dailyCap, setDailyCap] = useState('');
  const [ceiling, setCeiling] = useState('');
  const [ramp, setRamp] = useState('');
  // Warmup fields (warmup_settings table)
  const [warmup, setWarmup] = useState('');
  const [replyRate, setReplyRate] = useState('');
  const [dailyVolume, setDailyVolume] = useState('');
  const [targetVolume, setTargetVolume] = useState('');
  const [spamSave, setSpamSave] = useState('');
  const [markImportant, setMarkImportant] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    const capFields = {};
    if (dailyCap !== '') capFields.daily_cap = parseInt(dailyCap);
    if (ceiling !== '') capFields.daily_cap_ceiling = parseInt(ceiling);
    if (ramp !== '') capFields.ramp_pct = parseInt(ramp);

    const warmupFields = {};
    if (warmup !== '') warmupFields.enabled = warmup === 'on';
    if (replyRate !== '') warmupFields.reply_rate_pct = parseInt(replyRate);
    if (dailyVolume !== '') warmupFields.daily_volume = parseInt(dailyVolume);
    if (targetVolume !== '') warmupFields.target_volume = parseInt(targetVolume);
    if (ramp !== '') warmupFields.ramp_pct = parseInt(ramp);
    if (spamSave !== '') warmupFields.spam_save_enabled = spamSave === 'on';
    if (markImportant !== '') warmupFields.mark_important_enabled = markImportant === 'on';

    if (Object.keys(capFields).length === 0 && Object.keys(warmupFields).length === 0) {
      alert('Set at least one field'); return;
    }
    setSaving(true);
    try {
      const token = await getToken();
      if (Object.keys(capFields).length > 0) {
        const r1 = await fetch(`${API_BASE}/mailboxes/bulk-update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids: mailboxIds, fields: capFields }),
        });
        const d1 = await r1.json();
        if (!r1.ok) { alert('Caps failed: ' + (d1.error || r1.status)); setSaving(false); return; }
      }
      if (Object.keys(warmupFields).length > 0) {
        const r2 = await fetch(`${API_BASE}/mailboxes/warmup/bulk-update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids: mailboxIds, fields: warmupFields }),
        });
        const d2 = await r2.json();
        if (!r2.ok) { alert('Warmup failed: ' + (d2.error || r2.status)); setSaving(false); return; }
      }
      onDone();
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  const field = { display: 'flex', flexDirection: 'column', gap: 6 };
  const lbl = { fontSize: 11.5, fontWeight: 600, color: '#6b4a4a', textTransform: 'uppercase', letterSpacing: 0.4 };
  const inputBase = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1.5px solid #e4d3d3', fontSize: 14, boxSizing: 'border-box', background: '#fff', color: '#2a1a1a', outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s' };
  const onFocus = (e) => { e.target.style.borderColor = '#8B1A1A'; e.target.style.boxShadow = '0 0 0 3px rgba(139,26,26,0.12)'; };
  const onBlur = (e) => { e.target.style.borderColor = '#e4d3d3'; e.target.style.boxShadow = 'none'; };
  const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 18px' };
  const card = { border: '1px solid #f0e3e3', borderRadius: 12, padding: '18px 18px 20px', background: 'linear-gradient(180deg, #fdf8f8 0%, #ffffff 60%)' };
  const sectionHead = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: '#8B1A1A', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 16 };
  const dot = { width: 7, height: 7, borderRadius: '50%', background: '#8B1A1A' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(40,16,16,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: 660, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 70px rgba(60,10,10,0.3)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: '#8B1A1A', color: '#fff' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, letterSpacing: -0.2 }}>Bulk edit</h2>
            <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.8)', marginTop: 3 }}>{mailboxIds.length} mailboxes selected · blank fields stay unchanged</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'rgba(255,255,255,0.18)', width: 32, height: 32, borderRadius: 8, fontSize: 18, cursor: 'pointer', color: '#fff', lineHeight: 1 }}>&times;</button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18, background: '#faf6f6' }}>

          <div style={card}>
            <div style={sectionHead}><span style={dot} />Sending caps</div>
            <div style={grid}>
              <div style={field}><span style={lbl}>Daily cap</span><input type="number" value={dailyCap} onChange={e => setDailyCap(e.target.value)} onFocus={onFocus} onBlur={onBlur} placeholder="unchanged" style={inputBase} /></div>
              <div style={field}><span style={lbl}>Daily cap ceiling</span><input type="number" value={ceiling} onChange={e => setCeiling(e.target.value)} onFocus={onFocus} onBlur={onBlur} placeholder="unchanged" style={inputBase} /></div>
              <div style={field}><span style={lbl}>Ramp %</span><input type="number" value={ramp} onChange={e => setRamp(e.target.value)} onFocus={onFocus} onBlur={onBlur} placeholder="unchanged" style={inputBase} /></div>
            </div>
          </div>

          <div style={card}>
            <div style={sectionHead}><span style={dot} />Warmup</div>
            <div style={grid}>
              <div style={field}><span style={lbl}>Warmup enabled</span>
                <select value={warmup} onChange={e => setWarmup(e.target.value)} onFocus={onFocus} onBlur={onBlur} style={inputBase}>
                  <option value="">unchanged</option><option value="on">On</option><option value="off">Off</option>
                </select>
              </div>
              <div style={field}><span style={lbl}>Reply rate %</span><input type="number" value={replyRate} onChange={e => setReplyRate(e.target.value)} onFocus={onFocus} onBlur={onBlur} placeholder="unchanged" style={inputBase} /></div>
              <div style={field}><span style={lbl}>Daily volume</span><input type="number" value={dailyVolume} onChange={e => setDailyVolume(e.target.value)} onFocus={onFocus} onBlur={onBlur} placeholder="unchanged" style={inputBase} /></div>
              <div style={field}><span style={lbl}>Target volume</span><input type="number" value={targetVolume} onChange={e => setTargetVolume(e.target.value)} onFocus={onFocus} onBlur={onBlur} placeholder="unchanged" style={inputBase} /></div>
              <div style={field}><span style={lbl}>Spam-save</span>
                <select value={spamSave} onChange={e => setSpamSave(e.target.value)} onFocus={onFocus} onBlur={onBlur} style={inputBase}>
                  <option value="">unchanged</option><option value="on">On</option><option value="off">Off</option>
                </select>
              </div>
              <div style={field}><span style={lbl}>Mark important</span>
                <select value={markImportant} onChange={e => setMarkImportant(e.target.value)} onFocus={onFocus} onBlur={onBlur} style={inputBase}>
                  <option value="">unchanged</option><option value="on">On</option><option value="off">Off</option>
                </select>
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #f0e3e3', background: '#fff' }}>
          <button onClick={onClose} style={{ padding: '10px 18px', border: '1.5px solid #e4d3d3', borderRadius: 9, background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#6b4a4a' }}>Cancel</button>
          <button onClick={save} disabled={saving} onMouseEnter={e => !saving && (e.target.style.background = '#731515')} onMouseLeave={e => (e.target.style.background = '#8B1A1A')} style={{ padding: '10px 22px', border: 'none', borderRadius: 9, background: '#8B1A1A', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.6 : 1, boxShadow: '0 2px 8px rgba(139,26,26,0.3)' }}>
            {saving ? 'Saving...' : 'Apply to ' + mailboxIds.length}
          </button>
        </div>

      </div>
    </div>
  );
}

